// src/context/SensorContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { normalizeBay } from '../utils/bayHelpers';
import { useAuth } from './AuthContext';
import { useLocation } from '../contexts/LocationContext';

const SensorContext = createContext();

// Add the additional export that StatisticsContext expects
export const useSensorContext = () => {
  const context = useContext(SensorContext);
  if (!context) {
    throw new Error('useSensorContext must be used within a SensorProvider');
  }
  return context;
};

// Keep your existing useSensors export for backward compatibility
export const useSensors = () => {
  const context = useContext(SensorContext);
  if (!context) {
    throw new Error('useSensors must be used within a SensorProvider');
  }
  return context;
};

export const SensorProvider = ({ children }) => {
  const { currentUser, user: profile, supportPicking } = useAuth(); // Add authentication context
  const { selectedOrgId, selectedLocationId } = useLocation();
  const [sensorMappings, setSensorMappings] = useState({
    byChipSensor: {},
    byChemicalName: {},
    raw: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const transformSensorData = (sensors) => {
    const byChipSensor = {};
    const byChemicalName = {};

    sensors.forEach(sensor => {
      // Create chip/sensor lookup
      const chipId = sensor.chip;
      const sensorId = sensor.sensorId || sensor.sensor_id;
      const chipSensorKey = `${chipId}/${sensorId}`;
      byChipSensor[chipSensorKey] = {
        chemical_name: sensor.chemical_name,
        bay: sensor.bay ? normalizeBay(sensor.bay) : 'unknown',
        chipId: sensor.chip,
        sensorId: sensor.sensorId
      };

      // Create chemical_name lookup (handles multiple sensors per chemical)
      if (!byChemicalName[sensor.chemical_name]) {
        byChemicalName[sensor.chemical_name] = [];
      }
      byChemicalName[sensor.chemical_name].push({
        chip: chipId,
        sensor: sensorId,
        bay: sensor.bay ? normalizeBay(sensor.bay) : 'unknown',
        chipSensorKey
      });
    });

    return {
      byChipSensor,
      byChemicalName,
      raw: sensors
    };
  };

  const fetchSensorMappings = useCallback(async () => {
    // Check if user is authenticated
    if (!currentUser) {

      setLoading(false);
      return;
    }

    // Create user+org+location-specific cache key to prevent cross-user/org/location data leaks
    const cacheKey = `sensorMappings_${currentUser.email}_${selectedOrgId || 'default'}_${selectedLocationId || 'all'}`;

    setLoading(true);
    setError(null);

    try {
      // Use api service which handles auth automatically
      const sensors = await api.getSensors(selectedLocationId || null);

      // Validate we got the expected data
      if (!Array.isArray(sensors)) {
        throw new Error('Invalid sensor data format');
      }

      // Transform and store the data
      const transformed = transformSensorData(sensors);
      setSensorMappings(transformed);
      setLastRefreshed(new Date());

      // Cache in localStorage for offline use with user-specific key
      localStorage.setItem(cacheKey, JSON.stringify({
        data: transformed,
        timestamp: new Date().toISOString(),
        userEmail: currentUser.email // Store for verification
      }));

    } catch (err) {
      console.error('Error fetching sensor mappings:', err);
      setError(err.message);

      // Try to load from user-specific cache if available
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { data, timestamp, userEmail } = JSON.parse(cached);
          // Verify cache belongs to current user
          if (userEmail === currentUser.email) {
            setSensorMappings(data);
            setLastRefreshed(new Date(timestamp));

          } else {
            console.warn('[SensorContext] Cache email mismatch, ignoring cache');
          }
        } catch (cacheErr) {
          console.error('Failed to load cached mappings:', cacheErr);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedOrgId, selectedLocationId]); // Refetch when user, org, or location changes

  // Initial load - only fetch if user is authenticated (and, AC1, once the profile is known and a support login has
  // opened an account)
  useEffect(() => {
    if (currentUser && (!profile || supportPicking)) return;
    if (currentUser) {
      fetchSensorMappings();
    } else {
      // Clear all sensor data when user logs out to prevent cross-user data leaks

      setSensorMappings({
        byChipSensor: {},
        byChemicalName: {},
        raw: []
      });

      // Remove all sensor cache keys (both old generic and user-specific)
      // This ensures complete cleanup when switching users
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sensorMappings')) {
          localStorage.removeItem(key);
        }
      });

      setLoading(false);
    }
  }, [fetchSensorMappings, currentUser, profile, supportPicking]);

  // Helper function to get all sensors for a chemical in a specific bay
  const getSensorsForChemical = useCallback((chemical_name, bay) => {
    const sensors = sensorMappings.byChemicalName[chemical_name] || [];
    if (bay) {
      return sensors.filter(s => s.bay === bay);
    }
    return sensors;
  }, [sensorMappings]);

  // Helper function to get friendly name from chip/sensor
  const getChemicalName = useCallback((chipId, sensorId) => {
    const key = `${chipId}/${sensorId}`;
    return sensorMappings.byChipSensor[key]?.chemical_name || null;
  }, [sensorMappings]);

  // Add these properties that StatisticsContext expects
  const sensors = sensorMappings.raw;

  // Group sensors by bay for StatisticsContext compatibility
  const groupedSensors = useMemo(() => sensors.reduce((acc, sensor) => {
    const bay = sensor.bay || 'Unknown';
    if (!acc[bay]) {
      acc[bay] = [];
    }
    acc[bay].push(sensor);
    return acc;
  }, {}), [sensors]);

  const value = {
    // Core data
    sensorMappings,
    loading,
    error,
    lastRefreshed,

    // Add these for StatisticsContext compatibility
    sensors,
    groupedSensors,
    
    // Actions
    refreshMappings: fetchSensorMappings,
    
    // Helper functions
    getSensorsForChemical,
    getChemicalName,
    
    // Stats
    totalSensors: sensorMappings.raw.length,
    totalChemicals: Object.keys(sensorMappings.byChemicalName).length
  };

  return (
    <SensorContext.Provider value={value}>
      {children}
    </SensorContext.Provider>
  );
};
